// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import RugbyCarousel from "@rugby/App/Components/RugbyCarousel/RugbyCarousel";
import { carouselArrowStyle, swiperStyleWithArrowLine } from "@rugby/App/Components/RugbyCarousel/RugbyCarousel.style";
import XngCarousel from "@rugby/App/Components/XngCarousel/XngCarousel";
import { suppliersStyles } from "@rugby/App/Widgets/Suppliers/Suppliers.style";
import LazyImage from "@rugby/MobiusOverrides/LazyImage";
import Link from "@rugby/MobiusOverrides/Link";
import button from "@rugby/XDL/PreStyle/button";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsSelected = "brandsSelected",
    showBrandName = "showBrandName",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsSelected]: string[];
        [fields.showBrandName]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        select: "id,detailPagePath,logoSmallImagePath,logoAltText,name",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;
    const brandsPageLink = getPageLinkByPageType(state, "BrandsPage");

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        brandsPageLink,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

const styles = suppliersStyles;

interface SupplierProps {
    id: string;
    detailPagePath: string;
    logoSmallImagePath: string;
    logoAltText: string;
    name: string;
}

const SuppliersWidget: React.FC<Props> = ({
    fields,
    brandsState,
    shouldLoadBrandGallery,
    brandGalleryParameter,
    loadBrandGallery,
    brandsPageLink,
}) => {
    const [suppliers, setSuppliers] = React.useState([]);

    if (!brandsState || shouldLoadBrandGallery) {
        loadBrandGallery(brandGalleryParameter);
    }
    const filterTheSuppliersToFirstEight = () => {
        let tempSuppliers: any = [];
        tempSuppliers.length = 0;
        if (brandsState.value && fields.brandsSelected) {
            // eslint-disable-next-line
            tempSuppliers = brandsState.value.slice(0, 8);
        }
        setSuppliers(tempSuppliers);
    };

    React.useEffect(() => {
        filterTheSuppliersToFirstEight();
    }, [fields.brandsSelected, brandsState]);

    if (!brandsState || brandsState.isLoading || (brandsState.value?.length && !suppliers.length)) {
        return (
            <StyledWrapper {...styles.centeringWrapper} data-test-selector="supplier_loading_widget">
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper data-test-selector="supplier_widget">
            <Typography {...styles.TitleText} variant="h3">
                {fields.title}
            </Typography>
            <StyledWrapper {...styles.TitleLinkWrapper}>
                <Link {...styles.TitleLink} href={brandsPageLink?.url} data-test-selector="view_all_suppliers" withIcon>
                    {translate("View All Suppliers")}
                </Link>
            </StyledWrapper>
            <StyledWrapper>
                <RugbyCarousel
                    id="suppliers_carousel"
                    slidesPerView={1}
                    breakpoints={{
                        600: {
                            slidesPerView: 3,
                        },
                        900: {
                            slidesPerView: 4,
                        },
                        1024: {
                            slidesPerView: 5,
                        },
                        1300: {
                            slidesPerView: 7,
                        },
                    }}
                    spaceBetween={30}
                    navigationOutSide
                    extendedStyles={swiperStyleWithArrowLine}
                >
                    {suppliers.map((supplier: SupplierProps, index) => (
                        <StyledWrapper key={supplier.id}>
                            <StyledWrapper id={`item${supplier.id}`} data-test-selector="item_container">
                                <Link
                                    {...styles.supplierLink}
                                    href={supplier.detailPagePath}
                                    aria-label={supplier.name}
                                >
                                    <LazyImage
                                        {...styles.supplierImage}
                                        src={supplier.logoSmallImagePath}
                                        altText={supplier.logoAltText || supplier.name}
                                    />
                                </Link>
                                <Link
                                    {...styles.supplierLink}
                                    href={supplier.detailPagePath}
                                    aria-label={supplier.name}
                                >
                                    {fields.showBrandName && (
                                        <Typography {...styles.supplierText} variant="p">
                                            {supplier.name}
                                        </Typography>
                                    )}
                                </Link>
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </RugbyCarousel>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Xngage Custom" as any,
    displayName: "Rugby - Suppliers",
    fieldDefinitions: [
        {
            name: fields.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Translatable",
            isRequired: true,
        },
        {
            name: fields.brandsSelected,
            displayName: "Select brands",
            editorTemplate: "SelectBrandsField",
            defaultValue: [],
            fieldType: "General",
            isRequired: true,
            tooltip: "There will be a maximum of 8 Suppliers chosen for this spot. So only the first 8 will be chosen.",
        },
        {
            name: fields.showBrandName,
            displayName: "Show brands names",
            editorTemplate: "CheckboxField",
            defaultValue: false,
            fieldType: "General",
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SuppliersWidget),
    definition,
};

export default widgetModule;
